import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FolderClosed, FolderInput, MessageCircleOff, MoreHorizontal, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Composer } from "@/components/chat/Composer";
import { AssistantMessage, UserMessage } from "@/components/chat/Messages";
import { ConversationTitle } from "@/components/chat/ConversationTitle";
import { DeleteConversationDialog } from "@/components/chat/DeleteConversationDialog";
import { MoveToProjectDialog } from "@/components/projects/MoveToProjectDialog";
import { SendError } from "@/components/chat/SendError";
import { TopBarPortal } from "@/components/layout/TopBarSlot";
import { useConversation, useMe } from "@/hooks/queries";
import { useUploads } from "@/hooks/useUploads";
import { useResearchMode } from "@/hooks/useResearchMode";
import { ResearchMessage } from "@/components/research/ResearchMessage";
import { api } from "@/lib/api";
import { DEPTHS } from "@/lib/research";
import { mergeMessages, startStream, stopStream, useStreamEntry } from "@/lib/chatStore";

function useStickToBottom(ref, deps, resetKey) {
  const stick = useRef(true);
  useEffect(() => {
    stick.current = true;
  }, [resetKey]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [ref]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

function ChatHeader({ conversation, onDelete, onMove }) {
  return (
    <TopBarPortal>
      <div className="flex min-w-0 flex-1 items-center gap-1">
        {conversation?.project_id && (
          <Link to={`/projects/${conversation.project_id}`} className="hidden max-w-[180px] shrink-0 items-center gap-1.5 truncate rounded-md px-1.5 py-1 text-[13px] text-zinc-500 hover:text-zinc-200 sm:flex" data-testid="chat-project-breadcrumb">
            <FolderClosed className="h-3.5 w-3.5 shrink-0" strokeWidth={1.7} />
            <span className="truncate">{conversation.project_name}</span>
            <span className="text-zinc-700">/</span>
          </Link>
        )}
        <ConversationTitle conversation={conversation} />
        {conversation && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Conversation options" data-testid="conversation-options-button" className="rounded-md p-1.5 text-zinc-500 hover:bg-white/[0.05] hover:text-white">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="border-white/10 bg-ink-850">
              <DropdownMenuItem onSelect={onMove} data-testid="conversation-move-option" className="gap-2 focus:bg-white/[0.06]">
                <FolderInput className="h-4 w-4" /> Move to project
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onDelete} data-testid="conversation-delete-option" className="gap-2 text-rose-300 focus:bg-rose-500/10 focus:text-rose-200">
                <Trash2 className="h-4 w-4" /> Delete conversation
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </TopBarPortal>
  );
}

function NotFound() {
  return (
    <div className="flex h-full flex-col items-center justify-center px-6 text-center" data-testid="conversation-not-found">
      <MessageCircleOff className="h-8 w-8 text-zinc-600" strokeWidth={1.5} />
      <h2 className="mt-4 text-base font-medium text-white md:text-lg">Conversation not found</h2>
      <p className="mt-1.5 text-sm text-zinc-500">It may have been deleted, or it belongs to another account.</p>
      <Link to="/" className="mt-6 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink-950 hover:bg-white/90" data-testid="conversation-not-found-home">
        Start a new chat
      </Link>
    </div>
  );
}

export default function ChatPage() {
  const { conversationId } = useParams();
  const [params] = useSearchParams();
  const pendingKey = params.get("pending");
  const entry = useStreamEntry(conversationId || pendingKey);
  const navigate = useNavigate();
  const { data: me } = useMe();
  const query = useConversation(conversationId, { poll: !entry });
  const [draft, setDraft] = useState("");
  const uploads = useUploads();
  const research = useResearchMode();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [moving, setMoving] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!conversationId && entry?.conversationId) navigate(`/chat/${entry.conversationId}`, { replace: true });
    else if (!conversationId && !entry) navigate("/", { replace: true });
  }, [conversationId, entry, navigate]);

  const messages = mergeMessages(query.data?.messages ?? [], entry);
  const researchById = Object.fromEntries((query.data?.research ?? []).map((r) => [r.id, r]));
  const activeResearch = messages.find((m) => m.role === "assistant" && m.research_id && m.status === "streaming");
  const filesByMessage = {};
  (query.data?.files ?? []).forEach((f) => f.message_id && (filesByMessage[f.message_id] = [...(filesByMessage[f.message_id] || []), f]));
  const busy = Boolean(entry && ["connecting", "streaming"].includes(entry.status)) || Boolean(activeResearch);
  const outOfCredits = me && me.credits?.balance < 1;
  const last = messages[messages.length - 1];
  useStickToBottom(scrollRef, [messages.length, last?.content?.length, last?.status], conversationId);

  const send = () => {
    if (research.on) {
      const question = draft;
      setDraft("");
      research.start({ question, conversationId }).then((res) => !res && setDraft(question));
      return;
    }
    startStream({ conversationId: conversationId || null, content: draft, kind: "message", attachments: uploads.attachable, mode: research.chatMode });
    setDraft("");
    uploads.clear();
  };
  const stop = () => (activeResearch ? api.post(`/research/sessions/${activeResearch.research_id}/stop`).catch(() => {}) : stopStream(conversationId || pendingKey));
  const retry = () => conversationId && startStream({ conversationId, kind: "retry" });

  if (query.error?.status === 404) return <NotFound />;

  return (
    <div className="flex h-full flex-col" data-testid="chat-view-container">
      <ChatHeader conversation={query.data?.conversation} onDelete={() => setDeleteOpen(true)} onMove={() => setMoving(query.data?.conversation)} />
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto" data-testid="chat-scroll-area">
        <div className="mx-auto w-full max-w-[760px] space-y-7 px-4 pb-10 pt-8 sm:px-6">
          {query.isLoading && !entry && (
            <div className="space-y-6" data-testid="chat-loading">
              <div className="ml-auto h-10 w-2/5 animate-pulse rounded-2xl bg-white/[0.05]" />
              <div className="h-24 w-4/5 animate-pulse rounded-xl bg-white/[0.04]" />
            </div>
          )}
          {query.isError && query.error?.status !== 404 && !entry && (
            <div className="rounded-xl border border-white/10 bg-ink-850 p-4 text-sm text-zinc-300" data-testid="chat-load-error">
              {query.error.message}{" "}
              <button type="button" onClick={() => query.refetch()} className="underline underline-offset-4" data-testid="chat-load-retry">
                Try again
              </button>
            </div>
          )}
          {messages.map((m, i) =>
            m.role === "user" ? (
              <UserMessage key={m.id} message={m} attachments={filesByMessage[m.id]} badge={m.research_id && researchById[m.research_id] ? DEPTHS[researchById[m.research_id].depth]?.label : null} />
            ) : m.research_id && researchById[m.research_id] ? (
              <ResearchMessage key={m.id} message={m} research={researchById[m.research_id]} />
            ) : (
              <AssistantMessage key={m.id} message={m} isLast={i === messages.length - 1} canRetry={!busy && Boolean(conversationId)} onRetry={retry} />
            ),
          )}
          {entry?.status === "error" && (
            <SendError
              entry={entry}
              onEdit={(text) => (conversationId ? setDraft(text) : navigate("/", { replace: true, state: { draft: text } }))}
            />
          )}
        </div>
      </div>
      <div className="shrink-0 px-3 pb-[max(14px,env(safe-area-inset-bottom))] pt-2 sm:px-6">
        <div className="mx-auto w-full max-w-[760px]">
          <Composer
            ref={inputRef}
            value={draft}
            onChange={setDraft}
            onSubmit={send}
            onStop={stop}
            busy={busy}
            disabled={outOfCredits || !conversationId || research.starting}
            notice={outOfCredits ? "You're out of credits. Buying credit packs will be available soon." : research.starting ? "Starting your search…" : null}
            sendKey={me?.settings?.send_key}
            uploads={uploads}
            research={research}
            autoFocus
          />
          <p className="mt-2 text-center text-[11px] text-zinc-600">ChatSleek can make mistakes. Check important information.</p>
        </div>
      </div>
      {moving && <MoveToProjectDialog conversation={moving} onClose={() => setMoving(null)} />}
      {query.data?.conversation && (
        <DeleteConversationDialog conversation={query.data.conversation} open={deleteOpen} onOpenChange={setDeleteOpen} redirectTo="/" />
      )}
    </div>
  );
}
